import React from "react";
import { ScheduleEntry } from "../../Model/ImagingSchedule";
import { PlateAddress } from "../../Model/PlateAddress";
import { User } from "../../Model/User";
import DateUtil from "../../Utility/DateUtil";
import "./CommandPanel.css";
import { Plate } from "../../Model/Plate";

const CommandPanelContext = React.createContext<Context>({});

type Props = {
  style?: React.CSSProperties;
  children?: any;
};

type Context = {};

function CommandPanel({ style, children }: Props) {
  const context: Context = {};
  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",

        ...style,
      }}
    >
      <CommandPanelContext.Provider value={context}>
        {children}
      </CommandPanelContext.Provider>
    </div>
  );
}

export type BackArrowHeaderProps = {
  style?: React.CSSProperties;
  title: string;
  onBackArrowClick: () => void;
};

function BackArrowHeader({
  style,
  title,
  onBackArrowClick,
}: BackArrowHeaderProps) {
  return (
    <div
      className="back-arrow-header"
      style={{
        ...style,
      }}
    >
      <div className="back-arrow-header-arrow">
        <svg
          onClick={onBackArrowClick}
          width="48"
          height="48"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 24C4 21.9056 4.83198 19.897 6.31293 18.4161L14.771 9.95805C16.0484 8.68065 18.1194 8.68065 19.3968 9.95805L19.6774 10.2387C20.9548 11.516 20.9548 13.5871 19.6774 14.8645L14.0921 20.4498C13.9891 20.5529 14.0621 20.729 14.2078 20.729H39.9807C41.7873 20.729 43.2517 22.1935 43.2517 24C43.2517 25.8065 41.7873 27.271 39.9807 27.271H14.2078C14.0621 27.271 13.9891 27.4471 14.0921 27.5502L19.6774 33.1355C20.9548 34.4129 20.9548 36.4839 19.6774 37.7613L19.3968 38.042C18.1194 39.3193 16.0484 39.3193 14.771 38.042L6.31293 29.5839C4.83198 28.103 4 26.0944 4 24Z"
            fill="#F7A246"
          />
        </svg>
      </div>
      <div className="back-arrow-header-title">
        <span>{title}</span>
      </div>
    </div>
  );
}

function DarkBackArrowHeader({
  style,
  title,
  onBackArrowClick,
}: BackArrowHeaderProps) {
  return (
    <BackArrowHeader
      title={title}
      onBackArrowClick={onBackArrowClick}
      style={{ ...style, backgroundColor: "#414042" }}
    />
  );
}

export type TitleHeaderProps = {
  style?: React.CSSProperties;
  children?: any;
  title: string;
};

function TitleHeader({ style, children, title }: TitleHeaderProps) {
  return (
    <div
      className="command-panel-header"
      style={{
        ...style,
      }}
    >
      <div className="command-panel-header-title">{title}</div>
      <div className="command-panel-header-button-container">{children}</div>
    </div>
  );
}

export type PlateInfoHeaderProps = {
  style?: React.CSSProperties;
  children?: any;
  plateAddresses: PlateAddress[];
};

function PlateInfoHeader({
  style,
  children,
  plateAddresses,
}: PlateInfoHeaderProps) {
  let title = "No plate";
  let plate: Plate | null = null;

  for (let plateAddress of plateAddresses) {
    if (plateAddress.usedByPlate ?? plateAddress.reservedByPlate != null) {
      if (plate === null) {
        plate =
          plateAddress.usedByPlate ?? (plateAddress.reservedByPlate as Plate);
        title = `${plate.experimentName}_${plate.plateId}_${plate.barcode}`;
      } else {
        plate = null;
        title = "Multiple plates";
        break;
      }
    }
  }

  return (
    <div>
      <div
        className="command-panel-header"
        style={{
          ...style,
        }}
      >
        <div className="command-panel-header-title">{title}</div>
        <div className="command-panel-header-button-container">{children}</div>
      </div>
      {plate != null &&
        plate.errorStatusMessages != null &&
        plate.errorStatusMessages.length > 0 && (
          <div className="command-panel-message-container">
            <div className="command-panel-message-text-container">
              <div className="command-panel-message-text">
                {plate?.errorStatusMessages.map((esm) =>
                  esm.split("\n").map(function (item, idx) {
                    return (
                      <span key={idx}>
                        {item}
                        <br />
                      </span>
                    );
                  })
                )}
              </div>
            </div>
            <div className="error-exception-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7 24C7 14.6112 14.6112 7 24 7C33.3888 7 41 14.6112 41 24C41 33.3888 33.3888 41 24 41C14.6112 41 7 33.3888 7 24ZM26.4286 28.0475H21.5714L21.5714 13.4761H26.4286L26.4286 28.0475ZM21.5714 34.5238H26.4285V29.6666H21.5714V34.5238Z"
                  fill="#8C2228"
                />
              </svg>
            </div>
          </div>
        )}
    </div>
  );
}

export type ScheduleIInfoHeaderProps = {
  style?: React.CSSProperties;
  children?: any;
  imagingSchedules: ScheduleEntry[];
};

function ScheduleInfoHeader({
  style,
  children,
  imagingSchedules,
}: ScheduleIInfoHeaderProps) {
  let title = "No schedule";

  if (imagingSchedules != null && imagingSchedules.length > 0) {
    title =
      imagingSchedules.length > 1
        ? "Multiple schedules"
        : `${DateUtil.toUsFormatString(
            new Date(imagingSchedules[0].dateToImage)
          )}_${imagingSchedules[0].plateId}_${
            imagingSchedules[0].experimentName
          }`;
  }

  return (
    <div
      className="command-panel-header"
      style={{
        ...style,
      }}
    >
      <div className="command-panel-header-title">{title}</div>
      <div className="command-panel-header-button-container">{children}</div>
    </div>
  );
}

export type UsersInfoHeaderProps = {
  style?: React.CSSProperties;
  children?: any;
  users: User[];
};

function UsersInfoHeader({ style, children, users }: UsersInfoHeaderProps) {
  let title = "No Users";

  if (users != null && users.length > 0) {
    title =
      users.length > 1
        ? "Multiple Users"
        : `${users[0].userName}_${DateUtil.toUsFormatString(
            new Date(users[0].dateCreated)
          )}`; //TODO: Add Role
  }

  return (
    <div
      className="command-panel-header"
      style={{
        ...style,
      }}
    >
      <div className="command-panel-header-title">{title}</div>
      <div className="command-panel-header-button-container">{children}</div>
    </div>
  );
}

function VerticalContent({ style, children }: Props) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "7px",
        overflow: "auto",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function HorizontalContent({ style, children }: Props) {
  return (
    <div
      style={{
        display: "flex",
        padding: "16px 25px",
        flexWrap: "wrap",
        gap: 12,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

CommandPanel.PlateInfoHeader = PlateInfoHeader;
CommandPanel.ScheduleInfoHeader = ScheduleInfoHeader;
CommandPanel.UsersInfoHeader = UsersInfoHeader;
CommandPanel.BackArrowHeader = BackArrowHeader;
CommandPanel.DarkBackArrowHeader = DarkBackArrowHeader;
CommandPanel.TitleHeader = TitleHeader;
CommandPanel.VerticalContent = VerticalContent;
CommandPanel.HorizontalContent = HorizontalContent;

export default CommandPanel;
